import { useState, useCallback } from 'react';
import { Loader2, Search, Play, AlertTriangle, CheckCircle2, GitMerge, Settings2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { hostawayAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import { formatPropertyLabel } from '../../lib/propertyLabel';
import { HostawayListingMapModal } from './HostawayListingMapModal';

// dates_overlap_but_not_exact means the two sides' stay dates overlap but
// aren't identical (e.g. an off-by-one checkout correction on one side) --
// never proof of shared identity on its own, so it's always manual-review
// only, never auto-resolved by Apply. Styled distinctly so it isn't mistaken
// for the other (also-never-auto-resolved, but structurally different)
// reject reasons.
function ReasonBadges({ rejectReason }) {
  const reasons = (rejectReason || '').split('+').filter(Boolean);
  if (reasons.length === 0) return <span className="font-mono">&mdash;</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {reasons.map((r) => (
        <Badge
          key={r}
          variant={r === 'dates_overlap_but_not_exact' ? 'outline' : 'secondary'}
          className={r === 'dates_overlap_but_not_exact' ? 'text-amber-600 border-amber-300' : ''}
          title={r === 'dates_overlap_but_not_exact' ? 'Overlapping but not identical dates -- manual judgment only, never auto-resolved' : undefined}
        >
          {r}
        </Badge>
      ))}
    </div>
  );
}

export function MigrationAuditPanel() {
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [listingMapOpen, setListingMapOpen] = useState(false);

  const runPreview = useCallback(async () => {
    setPreviewing(true);
    setApplyResult(null);
    try {
      const { data } = await hostawayAPI.migrationAuditPreview();
      setPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  }, []);

  const runApply = useCallback(async () => {
    setConfirmOpen(false);
    setApplying(true);
    try {
      const { data } = await hostawayAPI.migrationAuditApply();
      setApplyResult(data);
      toast.success(
        `Applied: ${data.dedup?.total_superseded ?? 0} duplicate(s) resolved, ` +
        `${data.reservations?.updated ?? 0} Hostaway reservation(s) re-synced.`
      );
      // Refresh preview to reflect the new state.
      const { data: refreshed } = await hostawayAPI.migrationAuditPreview();
      setPreview(refreshed);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  }, []);

  const pending = preview?.duplicate_pairs?.pending_count ?? 0;
  const nearMiss = preview?.near_miss_pairs?.count ?? 0;
  const nearMissExcluded = preview?.near_miss_pairs?.excluded_unowned_count ?? 0;
  const zeroCommission = preview?.zero_commission_airbnb?.count ?? 0;
  const leakage = preview?.smoobu_post_cutover_leakage?.count ?? 0;
  const futureSkipped = preview?.smoobu_post_cutover_leakage?.future_skipped_count ?? 0;
  const existingUnresolved = preview?.smoobu_post_cutover_leakage?.existing_unresolved_count ?? 0;
  const excludedCancelled = preview?.smoobu_post_cutover_leakage?.excluded_cancelled_count ?? 0;
  const futureOrOutOfWindow = preview?.smoobu_post_cutover_leakage?.future_or_out_of_window_count ?? 0;
  const unclassifiedLeak = preview?.smoobu_post_cutover_leakage?.unclassified_count ?? 0;
  // `leakage` (future_skipped + counted_in_revenue) is the honest headline
  // number, but excluded_cancelled/future_or_out_of_window/unclassified rows
  // -- especially unclassified, which needs a manual live-check look -- can
  // be non-zero even when leakage itself is 0. Gating the whole section on
  // leakage alone would silently hide those rows instead of surfacing them.
  const hasLeakageSection = leakage > 0 || excludedCancelled > 0 || futureOrOutOfWindow > 0 || unclassifiedLeak > 0;
  const staleExpedia = preview?.stale_expedia_channel?.count ?? 0;
  const nothingToApply = preview && pending === 0 && zeroCommission === 0 && staleExpedia === 0;

  // The API caps every sample at 10 regardless of the true count. Never let
  // a capped table read as "the complete list" when it isn't.
  const truncationNote = (count, sampleLength) =>
    count > sampleLength ? `Showing first ${sampleLength} of ${count}.` : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <GitMerge className="h-5 w-5" />
            Hostaway/Smoobu Migration Audit
          </CardTitle>
          <Button
            data-testid="migration-audit-configure-btn"
            variant="ghost"
            size="sm"
            onClick={() => setListingMapOpen(true)}
          >
            <Settings2 className="h-4 w-4 mr-1.5" />
            Configure
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Duplicate bookings from the Smoobu-to-Hostaway migration
          {preview?.cutover_date ? ` (${preview.cutover_date})` : ''}, Airbnb
          reservations with the zero-commission bug, and Smoobu activity skipped as
          out-of-window. Preview is read-only; Apply re-syncs Hostaway (re-normalizing
          with corrected commission), resolves duplicates in Hostaway&apos;s favor, and
          re-syncs Smoobu.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button
            data-testid="migration-audit-preview-btn"
            onClick={runPreview}
            disabled={previewing || applying}
            variant="outline"
          >
            {previewing
              ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
              : <Search className="h-4 w-4 mr-2" />}
            Preview
          </Button>
          <Button
            data-testid="migration-audit-apply-btn"
            onClick={() => setConfirmOpen(true)}
            disabled={!preview || nothingToApply || applying || previewing}
            variant="default"
          >
            {applying
              ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
              : <Play className="h-4 w-4 mr-2" />}
            Apply
          </Button>
        </div>

        {preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Duplicate pairs pending</p>
                <p className="text-xl font-bold tabular-nums">{pending}</p>
              </div>
              <div className={`rounded-lg border p-3 ${nearMiss > 0 ? 'border-amber-300 dark:border-amber-800' : ''}`}>
                <p className="text-xs text-muted-foreground">Near-miss pairs</p>
                <p className="text-xl font-bold tabular-nums">{nearMiss}</p>
                {nearMissExcluded > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {`${nearMissExcluded} on unowned listings excluded`}
                  </p>
                )}
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Zero-commission Airbnb</p>
                <p className="text-xl font-bold tabular-nums">{zeroCommission}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Stale &quot;expedia&quot; channel</p>
                <p className="text-xl font-bold tabular-nums">{staleExpedia}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Smoobu post-cutover leakage</p>
                <p className="text-xl font-bold tabular-nums">{leakage}</p>
              </div>
            </div>

            {pending > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">
                  Duplicate pairs ({pending}) &mdash; Hostaway copy wins
                </h4>
                <p className="text-xs text-muted-foreground mb-2">
                  {preview.duplicate_pairs.note}
                  {truncationNote(pending, preview.duplicate_pairs.sample.length) && (
                    <span className="block mt-0.5">
                      {truncationNote(pending, preview.duplicate_pairs.sample.length)}
                    </span>
                  )}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left px-3 py-2 font-medium">Hostaway ID</th>
                        <th className="text-left px-3 py-2 font-medium">Smoobu ID (superseded)</th>
                        <th className="text-left px-3 py-2 font-medium">Match rule</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.duplicate_pairs.sample.map((m) => (
                        <tr key={`${m.hostaway_id}-${m.smoobu_id}`} className="border-b">
                          <td className="px-3 py-2 font-mono text-xs">{m.hostaway_id}</td>
                          <td className="px-3 py-2 font-mono text-xs">{m.smoobu_id}</td>
                          <td className="px-3 py-2 text-xs">{m.match_rule}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {(nearMiss > 0 || nearMissExcluded > 0) && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  Near-miss pairs ({nearMiss}) &mdash; not auto-resolved
                  {nearMissExcluded > 0 && (
                    <span className="text-xs font-normal text-muted-foreground">
                      {`· ${nearMissExcluded} pair${nearMissExcluded !== 1 ? 's' : ''} on unowned listings excluded`}
                    </span>
                  )}
                </h4>
                <p className="text-xs text-muted-foreground mb-2">
                  {preview.near_miss_pairs.note}
                  {truncationNote(nearMiss, preview.near_miss_pairs.sample.length) && (
                    <span className="block mt-0.5">
                      {truncationNote(nearMiss, preview.near_miss_pairs.sample.length)}
                    </span>
                  )}
                </p>
                {nearMiss > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="text-left px-3 py-2 font-medium">Guest</th>
                          <th className="text-left px-3 py-2 font-medium">Dates</th>
                          <th className="text-left px-3 py-2 font-medium">Hostaway property</th>
                          <th className="text-left px-3 py-2 font-medium">Smoobu property</th>
                          <th className="text-left px-3 py-2 font-medium">Reject reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.near_miss_pairs.sample.map((p) => (
                          <tr key={`${p.hostaway.id}-${p.smoobu.id}`} className="border-b">
                            <td className="px-3 py-2 text-xs">{p.guest_name}</td>
                            <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                              {p.check_in} &rarr; {p.check_out}
                              {p.reject_reason === 'dates_overlap_but_not_exact' && (
                                <div className="text-amber-600">
                                  sm: {p.smoobu.check_in} &rarr; {p.smoobu.check_out}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              <div>{p.hostaway.property_name}</div>
                              <div className="text-muted-foreground font-mono">
                                {p.hostaway.id} &middot; {p.hostaway.channel}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-xs">
                              <div>{p.smoobu.property_name}</div>
                              <div className="text-muted-foreground font-mono">
                                {p.smoobu.id} &middot; {p.smoobu.channel}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-xs"><ReasonBadges rejectReason={p.reject_reason} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {zeroCommission > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">
                  Zero-commission Airbnb reservations ({zeroCommission})
                </h4>
                <p className="text-xs text-muted-foreground mb-2">
                  {preview.zero_commission_airbnb.note}
                  {truncationNote(zeroCommission, preview.zero_commission_airbnb.sample.length) && (
                    <span className="block mt-0.5">
                      {truncationNote(zeroCommission, preview.zero_commission_airbnb.sample.length)}
                    </span>
                  )}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left px-3 py-2 font-medium">Hostaway ID</th>
                        <th className="text-left px-3 py-2 font-medium">Property</th>
                        <th className="text-left px-3 py-2 font-medium">Check-in</th>
                        <th className="text-right px-3 py-2 font-medium">Total price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.zero_commission_airbnb.sample.map((r) => (
                        <tr key={r.hostaway_id} className="border-b">
                          <td className="px-3 py-2 font-mono text-xs">{r.hostaway_id}</td>
                          <td className="px-3 py-2 text-xs">{formatPropertyLabel(r.property_name, r.property_id)}</td>
                          <td className="px-3 py-2 font-mono text-xs">{r.check_in}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {r.total_price != null ? Number(r.total_price).toFixed(2) : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {staleExpedia > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">
                  Stale &quot;expedia&quot; channel reservations ({staleExpedia})
                </h4>
                <p className="text-xs text-muted-foreground mb-2">
                  {preview.stale_expedia_channel.note}
                  {truncationNote(staleExpedia, preview.stale_expedia_channel.sample.length) && (
                    <span className="block mt-0.5">
                      {truncationNote(staleExpedia, preview.stale_expedia_channel.sample.length)}
                    </span>
                  )}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left px-3 py-2 font-medium">Hostaway ID</th>
                        <th className="text-left px-3 py-2 font-medium">Property</th>
                        <th className="text-left px-3 py-2 font-medium">Check-in</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.stale_expedia_channel.sample.map((r) => (
                        <tr key={r.hostaway_id} className="border-b">
                          <td className="px-3 py-2 font-mono text-xs">{r.hostaway_id}</td>
                          <td className="px-3 py-2 text-xs">{r.property_name}</td>
                          <td className="px-3 py-2 font-mono text-xs">{r.check_in}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {hasLeakageSection && (
              <div>
                <h4 className="text-sm font-medium mb-2">
                  Smoobu post-cutover leakage ({leakage})
                </h4>
                <p className="text-xs text-muted-foreground mb-2">
                  {preview.smoobu_post_cutover_leakage.note}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
                  <div className="rounded-lg border p-2">
                    <p className="text-xs text-muted-foreground">Already skipped going forward</p>
                    <p className="text-lg font-semibold tabular-nums">{futureSkipped}</p>
                  </div>
                  <div className={`rounded-lg border p-2 ${existingUnresolved > 0 ? 'border-amber-300 dark:border-amber-800' : ''}`}>
                    <p className="text-xs text-muted-foreground">Existing, unresolved right now</p>
                    <p className="text-lg font-semibold tabular-nums">{existingUnresolved}</p>
                  </div>
                  <div className="rounded-lg border p-2">
                    <p className="text-xs text-muted-foreground">Excluded, already cancelled</p>
                    <p className="text-lg font-semibold tabular-nums">{excludedCancelled}</p>
                  </div>
                  <div className="rounded-lg border p-2">
                    <p className="text-xs text-muted-foreground">Future / outside actuals window</p>
                    <p className="text-lg font-semibold tabular-nums">{futureOrOutOfWindow}</p>
                  </div>
                </div>
                {existingUnresolved > 0 && (
                  <div className="mb-3">
                    <h5 className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">
                      Still counted right now ({existingUnresolved})
                    </h5>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/30">
                            <th className="text-left px-3 py-2 font-medium">Smoobu ID</th>
                            <th className="text-left px-3 py-2 font-medium">Property</th>
                            <th className="text-left px-3 py-2 font-medium">Check-in</th>
                            <th className="text-left px-3 py-2 font-medium">Guest</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.smoobu_post_cutover_leakage.existing_unresolved_sample.map((r) => (
                            <tr key={r.smoobu_id} className="border-b">
                              <td className="px-3 py-2 font-mono text-xs">{r.smoobu_id}</td>
                              <td className="px-3 py-2 text-xs">{formatPropertyLabel(r.property_name, r.property_id)}</td>
                              <td className="px-3 py-2 font-mono text-xs">{r.check_in}</td>
                              <td className="px-3 py-2 text-xs">{r.guest_name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {truncationNote(existingUnresolved, preview.smoobu_post_cutover_leakage.existing_unresolved_sample.length) && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {truncationNote(existingUnresolved, preview.smoobu_post_cutover_leakage.existing_unresolved_sample.length)}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {unclassifiedLeak > 0 && (
                  <div className="mb-3">
                    <h5 className="text-xs font-medium flex items-center gap-1 text-amber-700 dark:text-amber-400 mb-1">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Needs a live look ({unclassifiedLeak})
                    </h5>
                    <p className="text-xs text-muted-foreground mb-1">
                      Not provably counted, cancelled, or merely future from stored data alone &mdash; run the
                      reservation-trace diagnostic (live Smoobu check) on these before treating them as leakage.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <tbody>
                          {preview.smoobu_post_cutover_leakage.unclassified_sample.map((r) => (
                            <tr key={r.smoobu_id} className="border-b">
                              <td className="px-3 py-2 font-mono text-xs">{r.smoobu_id}</td>
                              <td className="px-3 py-2 text-xs">{formatPropertyLabel(r.property_name, r.property_id)}</td>
                              <td className="px-3 py-2 font-mono text-xs">{r.check_in}</td>
                              <td className="px-3 py-2 text-xs">{r.guest_name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {truncationNote(unclassifiedLeak, preview.smoobu_post_cutover_leakage.unclassified_sample.length) && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {truncationNote(unclassifiedLeak, preview.smoobu_post_cutover_leakage.unclassified_sample.length)}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {(excludedCancelled > 0 || futureOrOutOfWindow > 0) && (
                  <details className="text-xs text-muted-foreground">
                    <summary className="cursor-pointer select-none">
                      {`${excludedCancelled} excluded as cancelled, ${futureOrOutOfWindow} future / outside window (informational, not leaking) -- show`}
                    </summary>
                    <div className="mt-2 space-y-3">
                      {excludedCancelled > 0 && (
                        <div className="overflow-x-auto">
                          <p className="font-medium mb-1">Excluded, already cancelled ({excludedCancelled})</p>
                          <table className="w-full text-sm">
                            <tbody>
                              {preview.smoobu_post_cutover_leakage.excluded_cancelled_sample.map((r) => (
                                <tr key={r.smoobu_id} className="border-b">
                                  <td className="px-3 py-2 font-mono text-xs">{r.smoobu_id}</td>
                                  <td className="px-3 py-2 text-xs">{formatPropertyLabel(r.property_name, r.property_id)}</td>
                                  <td className="px-3 py-2 font-mono text-xs">{r.check_in}</td>
                                  <td className="px-3 py-2 text-xs">{r.guest_name}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {truncationNote(excludedCancelled, preview.smoobu_post_cutover_leakage.excluded_cancelled_sample.length) && (
                            <p className="mt-1">
                              {truncationNote(excludedCancelled, preview.smoobu_post_cutover_leakage.excluded_cancelled_sample.length)}
                            </p>
                          )}
                        </div>
                      )}
                      {futureOrOutOfWindow > 0 && (
                        <div className="overflow-x-auto">
                          <p className="font-medium mb-1">Future / outside actuals window ({futureOrOutOfWindow})</p>
                          <table className="w-full text-sm">
                            <tbody>
                              {preview.smoobu_post_cutover_leakage.future_or_out_of_window_sample.map((r) => (
                                <tr key={r.smoobu_id} className="border-b">
                                  <td className="px-3 py-2 font-mono text-xs">{r.smoobu_id}</td>
                                  <td className="px-3 py-2 text-xs">{formatPropertyLabel(r.property_name, r.property_id)}</td>
                                  <td className="px-3 py-2 font-mono text-xs">{r.check_in}</td>
                                  <td className="px-3 py-2 text-xs">{r.guest_name}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {truncationNote(futureOrOutOfWindow, preview.smoobu_post_cutover_leakage.future_or_out_of_window_sample.length) && (
                            <p className="mt-1">
                              {truncationNote(futureOrOutOfWindow, preview.smoobu_post_cutover_leakage.future_or_out_of_window_sample.length)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </details>
                )}
              </div>
            )}

            {nothingToApply && (
              <div className="rounded-lg border p-4 text-center">
                <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
                <p className="text-sm text-muted-foreground">
                  No pending duplicates, zero-commission, or stale-channel records. Nothing to apply.
                </p>
              </div>
            )}
          </div>
        )}

        {applyResult && (
          <div className="rounded-lg border border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950/30 p-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium">
                Applied: {applyResult.dedup?.total_superseded ?? 0} duplicate(s) resolved,{' '}
                {applyResult.reservations?.updated ?? 0} Hostaway reservation(s) re-synced,{' '}
                {applyResult.smoobu?.out_of_window_skipped ?? 0} Smoobu record(s) out-of-window.
              </span>
            </div>
          </div>
        )}

        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm migration audit apply</DialogTitle>
              <DialogDescription>
                Re-syncs Hostaway listings and reservations (re-normalizing commission),
                resolves {pending} duplicate pair{pending !== 1 ? 's' : ''} in Hostaway&apos;s
                favor (Smoobu side marked superseded, not deleted), and re-syncs Smoobu.
              </DialogDescription>
            </DialogHeader>
            <div className="text-sm space-y-2 py-2">
              {zeroCommission > 0 && (
                <p className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="h-4 w-4" />
                  {zeroCommission} zero-commission Airbnb reservation{zeroCommission !== 1 ? 's' : ''} will
                  be recomputed from the live Hostaway API.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button data-testid="migration-audit-confirm-apply-btn" onClick={runApply}>Apply</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <HostawayListingMapModal
          open={listingMapOpen}
          onOpenChange={setListingMapOpen}
          onSaved={preview ? runPreview : undefined}
        />
      </CardContent>
    </Card>
  );
}
